import { Button } from "@component/button";
import { getApplicantMetrics, getJobMetrics } from "@service/job";
import { ApplicantMetrics, JobMetrics } from "@type/job";
import { useEffect, useState } from "react";
import {
    BriefcaseIcon,
    CalendarIcon,
    UsersIcon,
    CheckCircleIcon,
    ClockIcon,
    TimerIcon,
    SendIcon,
    CheckCircle2Icon,
    FileTextIcon,
    RefreshCwIcon,
    InfoIcon,
} from "lucide-react";
import ExecutiveSummaryCard from "@component/executive-summar-card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@component/select";
import { formatHumanReadable } from "@util/date";
import { Tooltip } from "@component/tooltip";

export default function CompanyMetrics() {
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [timePeriod, setTimePeriod] = useState("all");
    const [, setIsLoadingJobMetrics] = useState(false);
    const [, setIsLoadingApplicantMetrics] = useState(false);
    const [jobMetrics, setJobMetrics] = useState<JobMetrics>({
        last_updated_at: new Date().toISOString(),
        total_active_job_posting: 0,
        total_applications_received: 0,
        total_assessment_invitation_sent: 0,
        total_completion_rate: 0,
    })
    const [applicantMetrics, setApplicantMetrics] = useState<ApplicantMetrics>({
        total_candidate_entered_review: 0,
        median_time_to_review_cv_days: 0,
        median_time_to_review_cv_hours: 0,
        median_time_to_review_assessment_days: 0,
        median_time_to_review_assessment_hours: 0,
        offer_to_hire_rate: 0,
        total_time_to_fill_days: 0,
        total_time_to_fill_hours: 0,
        total_interview_rate: 0,
    })

    useEffect(() => {
        setIsLoadingJobMetrics(true);
        getJobMetrics(timePeriod).then((resp) => {
            if (resp.status === 200) {
                setJobMetrics(resp.data.data);
            }
        }).finally(() => {
            setIsLoadingJobMetrics(false);
        });

        setIsLoadingApplicantMetrics(true);
        getApplicantMetrics(timePeriod).then((resp) => {
            if (resp.status === 200) {
                setApplicantMetrics(resp.data.data);
            }
        }).finally(() => {
            setIsLoadingApplicantMetrics(false);
        });
    }, [timePeriod]);

    const handleRefresh = () => {
        setIsRefreshing(true);
        getJobMetrics(timePeriod).then((resp) => {
            if (resp.status === 200) {
                setJobMetrics(resp.data.data);
            }
        }).finally(() => {
            setIsRefreshing(false);
        });
    };

    return (
        <div className="space-y-6">
            <div>
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <h2 className="text-2xl font-semibold text-foreground">
                            Hiring Analytics
                        </h2>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground">
                            Showing metrics for:
                        </span>
                        <Select value={timePeriod} onValueChange={setTimePeriod}>
                            <SelectTrigger className="h-8 w-[140px] text-xs">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="7">Last 7 days</SelectItem>
                                <SelectItem value="30">Last 30 days</SelectItem>
                                <SelectItem value="90">Last 90 days</SelectItem>
                                <SelectItem value="all">All-time</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <p className="text-sm text-muted-foreground">
                        Last updated: {formatHumanReadable(jobMetrics.last_updated_at, true)}
                    </p>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 hover:bg-transparent"
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                    >
                        <RefreshCwIcon
                            className={`h-3 w-3 text-muted-foreground hover:text-foreground transition-colors ${isRefreshing ? "animate-spin" : ""
                                }`}
                        />
                    </Button>
                </div>
            </div>
            {/* Application & Assessment Pipeline */}
            <div>
                <div className="mb-4">
                    <div className="flex flex-row items-center gap-2">
                        <h3 className="text-lg font-semibold text-foreground">
                            Application & Screening Pipeline
                        </h3>
                        <Tooltip side="right" content="Track application volume and screening engagement.">
                            <InfoIcon className="w-4 h-4 text-gray-500" />
                        </Tooltip>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Job Application Section */}
                    <div className="lg:col-span-2 space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <ExecutiveSummaryCard
                                label="Active Job Listings"
                                value={jobMetrics.total_active_job_posting}
                                icon={BriefcaseIcon}
                                tooltip="Number of jobs that are currently open and accepting applications."
                            />

                            <ExecutiveSummaryCard
                                label="Total Applications Received"
                                value={jobMetrics.total_applications_received}
                                icon={FileTextIcon}
                                tooltip="Total number of applications submitted across all active jobs in the selected period."
                            />
                        </div>
                    </div>

                    {/* Assessment Flow Section */}
                    <div className="lg:col-span-2 space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <ExecutiveSummaryCard
                                label="AI Screening Invitations Sent"
                                value={jobMetrics.total_assessment_invitation_sent}
                                icon={SendIcon}
                                tooltip="Number of candidates invited to complete an AI screening."
                            />

                            <ExecutiveSummaryCard
                                label="AI Screening Completion Rate"
                                value={jobMetrics.total_completion_rate.toFixed(0)}
                                icon={CheckCircle2Icon}
                                suffix="%"
                                tooltip="Percentage of invited candidates who completed the AI screening."
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Review, Decision & Performance Metrics */}
            <div>
                <div className="mb-4">
                    <div className="flex flex-row items-center gap-2">
                        <h3 className="text-lg font-semibold text-foreground">
                            Review, Decision & Performance Metrics
                        </h3>
                        <Tooltip side="right" content="Measure review capacity, decision timelines, and hiring outcomes.">
                            <InfoIcon className="w-4 h-4 text-gray-500" />
                        </Tooltip>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <ExecutiveSummaryCard
                        label="Candidates Entered Review"
                        value={applicantMetrics.total_candidate_entered_review.toFixed(0)}
                        icon={UsersIcon}
                        tooltip="Number of candidates entered Pending Review status during the period."
                    />

                    <ExecutiveSummaryCard
                        label="Median Time to Review CV"
                        value={applicantMetrics.median_time_to_review_cv_days === 0 ? "N/A" : (applicantMetrics.median_time_to_review_cv_days >= 1 ? applicantMetrics.median_time_to_review_cv_days.toFixed(0) : (applicantMetrics.median_time_to_review_cv_hours >= 1 ? applicantMetrics.median_time_to_review_cv_hours.toFixed(0) : "<1"))}
                        icon={TimerIcon}
                        suffix={applicantMetrics.median_time_to_review_cv_days === 0 ? "" : (applicantMetrics.median_time_to_review_cv_days >= 1 ? "days" : (applicantMetrics.median_time_to_review_cv_hours >= 1 ? "hours" : "hour"))}
                        tooltip="Median time from application until the CV is reviewed and a screening invitation or decision is made."
                    />

                    <ExecutiveSummaryCard
                        label="Median Time to Review AI Screening"
                        value={applicantMetrics.median_time_to_review_assessment_days === 0 ? "N/A" : (applicantMetrics.median_time_to_review_assessment_days >= 1 ? applicantMetrics.median_time_to_review_assessment_days.toFixed(0) : (applicantMetrics.median_time_to_review_assessment_hours >= 1 ? applicantMetrics.median_time_to_review_assessment_hours.toFixed(0) : "<1"))}
                        icon={ClockIcon}
                        suffix={applicantMetrics.median_time_to_review_assessment_days === 0 ? "" : (applicantMetrics.median_time_to_review_assessment_days >= 1 ? "days" : (applicantMetrics.median_time_to_review_assessment_hours >= 1 ? "hours" : "hour"))}
                        tooltip="Median time taken from when a candidate completes a screening until a review action is taken."
                    />

                    <ExecutiveSummaryCard
                        label="Offer-to-Hire Rate"
                        value={applicantMetrics.offer_to_hire_rate.toFixed(0)}
                        icon={CheckCircleIcon}
                        suffix="%"
                        tooltip="Percentage of candidates who were marked as hired."
                    />

                    <ExecutiveSummaryCard
                        label="Time to Fill"
                        value={applicantMetrics.total_time_to_fill_days === 0 ? "N/A" : (applicantMetrics.total_time_to_fill_days >= 1 ? applicantMetrics.total_time_to_fill_days.toFixed(0) : (applicantMetrics.total_time_to_fill_hours >= 1 ? applicantMetrics.total_time_to_fill_hours.toFixed(0) : "<1"))}
                        icon={CalendarIcon}
                        suffix={applicantMetrics.total_time_to_fill_days === 0 ? "" : (applicantMetrics.total_time_to_fill_days >= 1 ? "days" : (applicantMetrics.total_time_to_fill_hours >= 1 ? "hours" : "hour"))}
                        tooltip={`Median number of ${(applicantMetrics.total_time_to_fill_hours === 0 && applicantMetrics.total_time_to_fill_days) === 0 ? "days" : "hours"} from job posting date to offer accepted date.`}
                    />

                    <ExecutiveSummaryCard
                        label="Interview Rate"
                        value={applicantMetrics.total_interview_rate.toFixed(0)}
                        icon={CalendarIcon}
                        suffix="%"
                        tooltip="Percentage of applications invited to interview during the selected period."
                    />
                </div>
            </div>
        </div>
    )
}